import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { selectOnboardingBaseUrl } from '../store/selectors/onboarding-config.selectors';
import { selectBffBaseUrl } from '../store/selectors/api-config.selectors';
import { TranslocoService } from '@jsverse/transloco';

@Injectable()
export class ApiInterceptor implements HttpInterceptor {
  private bffBaseUrl$: Observable<string>;
  private blackListBasesUrls: string[] = [];

  constructor(
    private translocoService: TranslocoService,
    private store: Store,
  ) {
    /** @todo create public method to populate blacklist */
    this.store
      .pipe(selectOnboardingBaseUrl)
      .subscribe((onboardingBaseUrl: string) => {
        if (
          onboardingBaseUrl &&
          !this.blackListBasesUrls.includes(onboardingBaseUrl)
        ) {
          this.blackListBasesUrls.push(onboardingBaseUrl);
        }
      });

    this.bffBaseUrl$ = this.store.pipe(selectBffBaseUrl);
  }

  /**
   * Intercept and modify the http requests globally before they are sent to the server.
   *
   * @param req the request to modify.
   * @param next transforms an HttpRequest into a stream of HttpEvents
   * @return the modified request
   */
  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    let headers = req.headers;

    if (!this.shouldHandleUrl(req.url)) {
      return next.handle(req);
    }

    if (!headers.has('Content-Type')) {
      headers = headers.set('Content-Type', 'application/json');
    }

    if (!headers.has('Accept')) {
      headers = headers.set('Accept', 'application/json');
    }

    if (!headers.has('Cache-Control')) {
      headers = headers.set('Cache-Control', 'no-cache');
    }

    if (!headers.has('Accept-Language')) {
      const lang = this.translocoService.getActiveLang();
      if (lang) {
        headers = headers.set('Accept-Language', lang.split('-')[0]);
      }
    }

    if (!headers.has('X-B3-TraceId')) {
      headers = headers.set('X-B3-TraceId', makeid(32));
    }
    if (!headers.has('X-B3-SpanId')) {
      headers = headers.set('X-B3-SpanId', makeid(16));
    }

    return this.bffBaseUrl$.pipe(
      switchMap((baseUrl) => {
        const fullUrl = getFullUrl(req.url, baseUrl);

        const request = req.clone({
          url: fullUrl,
          headers,
          withCredentials: true,
        });

        return next.handle(request);
      }),
    );
  }

  /**
   * Check if the passed url should be handled by this api interceptor
   * @param url
   * @returns true if the url should be handled
   */
  private shouldHandleUrl(url: string): boolean {
    return !(
      isAbsoluteUrl(url) &&
      this.blackListBasesUrls.some((baseUrl) => url.startsWith(baseUrl))
    );
  }
}

const absoluteUrlRegex = new RegExp('^(?:[a-z]+:)?//', 'i');
const isAbsoluteUrl = (url: string): boolean => absoluteUrlRegex.test(url);
const isRelativeToLocalUrl = (url: string): boolean => url.startsWith('.');

/**
 * Returns same url without changes if:
 *     url is absolute or url is relative to local (starts with '.')
 *
 * Otherwise prepends baseUrl
 *
 * @param url the request's url
 * @return new url string
 */
function getFullUrl(url: string, baseUrl: string): string {
  const pathSeparator = url.startsWith('/') ? '' : '/';

  return isAbsoluteUrl(url) || isRelativeToLocalUrl(url)
    ? url
    : baseUrl + pathSeparator + url;
}

function makeid(length) {
  let result = '';
  const characters = 'abcdef0123456789';
  const charactersLength = characters.length;
  let counter = 0;
  while (counter < length) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
    counter += 1;
  }
  return result;
}
